import { FetchedWasteTypes } from "@/Utils/api/sanity/types";
import { removeMoreThanTwoDecimals } from "@/Utils/formatFunctions";
import { sortAlphabeticallyByProperty } from "@/Utils/sorting";
import { sortByOrderRank } from "@/Utils/wasteStreams";
import { WasteTypeConfig } from "@/pages/configuration/useConfigService";

import { GetCustomersResponse } from "server/dwhApi/customers/models.js";
import { GetAccessCodesResponse } from "server/scannerApi/customers/models.js";

import {
  CollapsableTableDataInterface,
  WasteTypeDetails,
} from "./customerDetails.interface";

type PeriodSummary = {
  recyclingRate: number;
  wasteTypes: number;
  throws: number;
  residualWaste: number;
  totalWeight: number;
};

export const calcAverages = (
  firstPeriod: GetCustomersResponse,
  currentPeriod: GetCustomersResponse,
  tenantCategoryValue?: string,
) => {
  const initSummary: PeriodSummary = {
    recyclingRate: 0,
    wasteTypes: 0,
    throws: 0,
    residualWaste: 0,
    totalWeight: 0,
  };
  const firstPeriodCustomers = firstPeriod.customers.filter(
    (c) =>
      !tenantCategoryValue ||
      c.commercialCustomerCategory === tenantCategoryValue,
  );
  const currentPeriodCustomers = currentPeriod.customers.filter(
    (c) =>
      !tenantCategoryValue ||
      c.commercialCustomerCategory === tenantCategoryValue,
  );

  const sumsPrevPeriod: PeriodSummary = firstPeriodCustomers.reduce(
    (acc: PeriodSummary, el) => {
      return {
        recyclingRate: acc.recyclingRate + el.sortingDegree,
        wasteTypes: acc.wasteTypes + el.wasteTypes.length,
        throws: acc.throws + el.totalOccurrences,
        residualWaste: acc.residualWaste + el.totalResidualWasteWeight.quantity,
        totalWeight: acc.totalWeight + el.totalWasteWeight.quantity,
      };
    },
    initSummary,
  );

  const sums: PeriodSummary = currentPeriodCustomers.reduce(
    (acc: PeriodSummary, el) => {
      return {
        recyclingRate: acc.recyclingRate + el.sortingDegree,
        wasteTypes: acc.wasteTypes + el.wasteTypes.length,
        throws: acc.throws + el.totalOccurrences,
        residualWaste: acc.residualWaste + el.totalResidualWasteWeight.quantity,
        totalWeight: acc.totalWeight + el.totalWasteWeight.quantity,
      };
    },
    initSummary,
  );

  const prevActiveTenants = firstPeriodCustomers.length;
  const activeTenants = currentPeriodCustomers.length;

  const totalWeight = removeMoreThanTwoDecimals(
    sums.totalWeight / activeTenants,
  );
  const residualWaste = removeMoreThanTwoDecimals(
    sums.residualWaste / activeTenants,
  );

  const totalWeightPrev = removeMoreThanTwoDecimals(
    sumsPrevPeriod.totalWeight / prevActiveTenants,
  );
  const residualWastePrev = removeMoreThanTwoDecimals(
    sumsPrevPeriod.residualWaste / prevActiveTenants,
  );

  const recyclingRate = ((totalWeight - residualWaste) / totalWeight) * 100;
  const prevRecyclingRate =
    ((totalWeightPrev - residualWastePrev) / totalWeightPrev) * 100;

  return {
    recyclingRate,
    wasteTypes: removeMoreThanTwoDecimals(sums.wasteTypes / activeTenants),
    throws: removeMoreThanTwoDecimals(sums.throws / activeTenants),
    residualWaste,
    totalWeight,
    percentageChange: removeMoreThanTwoDecimals(
      recyclingRate - prevRecyclingRate,
    ),
  };
};

export const getTenantDetailsTableData = ({
  firstPeriod,
  currentPeriod,
  sanityData,
  wasteTypeConfig,
  tenantCategoryValue,
  accessCodes,
}: {
  firstPeriod: GetCustomersResponse;
  currentPeriod: GetCustomersResponse;
  sanityData: FetchedWasteTypes;
  wasteTypeConfig: WasteTypeConfig[];
  tenantCategoryValue?: string;
  accessCodes?: GetAccessCodesResponse;
}): CollapsableTableDataInterface[] =>
  currentPeriod.customers
    .filter((c) =>
      tenantCategoryValue
        ? c.commercialCustomerCategory === tenantCategoryValue
        : true,
    )
    .map((t) => {
      const details: WasteTypeDetails[] = sortByOrderRank(
        t.wasteTypes.map((wt) => ({
          wasteCode: wt.code,
          throws: wt.occurrences,
          totalWeight: wt.weight.quantity,
          wasteType:
            (wasteTypeConfig.find((wtc) => wtc.wasteTypeCode === wt.code)
              ?.name ||
              sanityData.find((el) => el.id === wt.code)?.name) ??
            wt.code,
          share: (wt.weight.quantity * 100) / t.totalWasteWeight.quantity,
        })),
        sanityData,
        "wasteCode",
      );

      const tenantLastPeriod = firstPeriod.customers.find(
        (el) => el.customerName === t.customerName,
      );
      const previousAmount = (tenantLastPeriod?.sortingDegree ?? 0) * 100;
      const currentSortingDegree = t.sortingDegree * 100;
      const diff = currentSortingDegree - previousAmount;

      const tenantCode =
        accessCodes?.data?.find((el) => el.storeUuid === t.customerId)
          ?.accessCode || "";

      return {
        details,
        id: t.customerId,
        name: t.customerName,
        recyclingRate: removeMoreThanTwoDecimals(t.sortingDegree * 100),
        throws: t.totalOccurrences,
        totalWeight: t.totalWasteWeight.quantity,
        wasteTypes: t.wasteTypes.length,
        residualWaste: t.totalResidualWasteWeight.quantity,
        percentageChange: diff,
        categoryId: t.commercialCustomerCategory,
        tenantCode,
      };
    })
    .sort(sortAlphabeticallyByProperty);
